import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Copy, ExternalLink, Check, KeyRound } from "lucide-react";

/**
 * Microsites directory — every gated pitch/case-study page in one place with
 * its live URL and password, copy-to-clipboard for fast sharing. Gated to the
 * owner (REVIEWS_ALLOWED_EMAILS) since it exposes passwords. The list is served
 * by GET /microsites (single source of truth in server.py).
 */

const KIND_COLOR = {
  pitch: "#2563eb", "case study": "#7c3aed", campaign: "#d97706", tool: "#0f766e",
};

export default function MicrositesDirectory() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/microsites");
        setSites(res.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load directory");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(""), 1400);
    } catch {
      toast.error("Copy failed — copy manually");
    }
  };

  const copyBoth = (s) => copy(`${s.url}\nPassword: ${s.password}`, `both-${s.path}`);

  return (
    <div className="dm-page-container" style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--t3)", fontSize: 13, marginBottom: 16, background: "none", border: "none", cursor: "pointer" }}>
        <ArrowLeft size={16} /> BACK
      </button>

      <h1 style={{ fontSize: 26, fontWeight: 800, display: "flex", alignItems: "center", gap: 10 }}>
        <KeyRound size={24} /> Microsites & passwords
      </h1>
      <p style={{ color: "var(--t3)", fontSize: 13, marginTop: 4, marginBottom: 22 }}>
        Every gated pitch and case-study page, with its live link and password. Tap to copy.
      </p>

      {loading ? (
        <p style={{ color: "var(--t3)" }}>Loading…</p>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {sites.map((s) => (
            <div key={s.path} style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: "14px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</span>
                    <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: KIND_COLOR[s.kind] || "#666", border: `1px solid ${KIND_COLOR[s.kind] || "#666"}55`, borderRadius: 5, padding: "1px 7px" }}>{s.kind}</span>
                  </div>
                  <a href={s.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: "#7ca6f7", marginTop: 4 }}>
                    {s.url} <ExternalLink size={12} />
                  </a>
                </div>
                <button onClick={() => copyBoth(s)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", background: "#1a56db", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {copied === `both-${s.path}` ? <Check size={15} /> : <Copy size={15} />}
                  {copied === `both-${s.path}` ? "Copied" : "Copy link + pw"}
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--bdr)" }}>
                <span style={{ fontSize: 11, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".05em" }}>Password</span>
                <code style={{ fontSize: 13, fontFamily: "var(--font-mono, monospace)", background: "var(--s2)", padding: "3px 10px", borderRadius: 6, color: "var(--t1)" }}>{s.password}</code>
                <button onClick={() => copy(s.password, `pw-${s.path}`)} aria-label="Copy password" style={{ padding: 6, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 6, color: "var(--t2)", cursor: "pointer" }}>
                  {copied === `pw-${s.path}` ? <Check size={13} /> : <Copy size={13} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
